import { Checkbox } from '@monorepo/shared/componentsV2/Checkbox';
import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Search } from '@monorepo/shared/componentsV2/Search';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useProjectUserOptions } from '@monorepo/shared/hooks/useProjectUserOptions';
import { bodyRegular } from '@monorepo/shared/styles/text';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const MAX_VISIBLE_OPTIONS = 100;

const SearchSection = styled.div`
  padding: 0.75rem 1rem;
`;

const SelectedUsers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0 1rem 0.75rem;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 15rem;
  padding: 0 1rem 0.75rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.p`
  ${bodyRegular}
  padding: 0.5rem 1rem 1rem;
  color: ${({ theme }) => theme.colors.gray666};
`;

const ErrorMessage = styled.p`
  ${bodyRegular}
  padding: 0.5rem 1rem 1rem;
  color: ${({ theme }) => theme.colors.red};
`;

const TruncationMessage = styled.p`
  ${bodyRegular}
  padding: 0.5rem 0 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.gray666};
`;

const LoadingContainer = styled.div`
  padding: 1.5rem 0;
`;

interface AssigneeFilterPanelProps {
  onChange: (nextUserIds: string[]) => void;
  selectedUserIds: string[];
}

export function AssigneeFilterPanel({
  onChange,
  selectedUserIds,
}: AssigneeFilterPanelProps) {
  const [search, setSearch] = useState('');

  const { project } = useCurrentProject();
  const { userOptions, isLoading, isError } = useProjectUserOptions({
    projectId: project?.id,
    organizationId: project?.organization_id,
    previouslySavedUserIds: selectedUserIds,
  });

  const { filteredOptions, matchCount, selectedOptions } = useMemo(() => {
    const options = userOptions ?? [];
    const query = search.trim().toLowerCase();
    const matches = query
      ? options.filter((option) => option.label.toLowerCase().includes(query))
      : options;
    const selectedIdSet = new Set(selectedUserIds);
    const visibleOptions = matches.slice(0, MAX_VISIBLE_OPTIONS);

    return {
      filteredOptions: visibleOptions,
      matchCount: matches.length,
      selectedOptions: options.filter((option) =>
        selectedIdSet.has(option.value),
      ),
    };
  }, [search, selectedUserIds, userOptions]);

  const handleToggle = (value: string, isChecked: boolean) => {
    const nextUserIds = isChecked
      ? [...selectedUserIds, value]
      : selectedUserIds.filter((id) => id !== value);
    onChange(nextUserIds);
  };

  return (
    <div>
      <SearchSection>
        <Search
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search"
          value={search}
        />
      </SearchSection>
      {selectedOptions.length > 0 && (
        <SelectedUsers>
          {selectedOptions.map((option) => (
            <Chip
              key={option.value}
              color="secondary"
              label={option.label}
              onDelete={() => handleToggle(option.value, false)}
            />
          ))}
        </SelectedUsers>
      )}
      {isLoading && (
        <LoadingContainer>
          <Loading size="small" />
        </LoadingContainer>
      )}
      {!isLoading && isError && (
        <ErrorMessage>
          Failed to load assignees. Please refresh and try again.
        </ErrorMessage>
      )}
      {!isLoading && !isError && filteredOptions.length === 0 && (
        <EmptyMessage>No assignees found.</EmptyMessage>
      )}
      {!isLoading && !isError && filteredOptions.length > 0 && (
        <OptionList>
          {filteredOptions.map((option) => (
            <Checkbox
              key={option.value}
              checked={selectedUserIds.includes(option.value)}
              label={option.label}
              onChange={(event) =>
                handleToggle(option.value, event.target.checked)
              }
            />
          ))}
          {filteredOptions.length < matchCount && (
            <TruncationMessage>
              Showing {filteredOptions.length} of {matchCount} users
            </TruncationMessage>
          )}
        </OptionList>
      )}
    </div>
  );
}
